import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { debounce } from "lodash";
import {
  Course,
  QueryParams,
  OfflineQueueItem,
  FetchCoursesResponse,
  CourseState,
} from "./courseTypes";
import { CourseFormData } from "./courseTypes";
import apiService from "../../services/api.services";
import { safeValidate, FetchCoursesResponseSchema } from "../../schemas/api.schemas";

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const OFFLINE_STORAGE_KEY = "course_offline_data";

// Helper to create cache key from query params
const createCacheKey = (params: QueryParams): string => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
};

const initialState: CourseState = {
  courses: {},
  publicCourses: {},
  status: "idle",
  error: null,
  total: 0,
  currentPage: 1,
  courseId: null,
  lastFetched: {},
  pendingUpdates: {},
  offlineQueue: [],
  activeRequests: {},
  retryCount: {},
};

// Helper function to handle offline storage
const saveToOfflineStorage = (data: Record<number, Course>): void => {
  try {
    localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save to offline storage:", error);
  }
};

const loadFromOfflineStorage = (): Record<number, Course> | null => {
  try {
    const data = localStorage.getItem(OFFLINE_STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Failed to load from offline storage:", error);
    return null;
  }
};

// Debounced fetch function
const debouncedFetch = debounce(
  (params: QueryParams, thunkAPI: any) => {
    return fetchCourses(params)(thunkAPI.dispatch, thunkAPI.getState, null);
  },
  500,
  { leading: true, trailing: false }
);

export const createCourse = createAsyncThunk<
  Course,
  CourseFormData,
  {
    rejectValue: { message: string };
  }
>("courses/createCourse", async (courseData, { rejectWithValue }) => {
  try {
    const response = await apiService.post<{ data: Course }>(
      `/courses/create-course`,
      courseData
    );

    toast.success("Course created successfully!");
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    const errorMessage =
      axiosError.response?.data?.message || "Failed to create course";
    toast.error(errorMessage);
    return rejectWithValue({ message: errorMessage });
  }
});
// Enhanced fetch courses with concurrency control, retry logic, and offline support
export const fetchCourses = createAsyncThunk<
  {
    courses: Course[];
    pagination: {
      total: number;
      currentPage: number;
      totalPages: number;
    };
    cacheKey: string;
  } | null,
  QueryParams,
  {
    state: { courses: CourseState };
    rejectValue: { message: string };
  }
>(
  "courses/fetchCourses",
  async (params, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const cacheKey = createCacheKey(params);
    const requestId = `fetch_${cacheKey}`;

    // Check if request is already in progress
    if (state.courses.activeRequests[requestId]) {
      return null; // Skip duplicate request
    }

    // Check offline status
    if (!navigator.onLine) {
      const offlineData = loadFromOfflineStorage();
      if (offlineData) {
        return {
          courses: Object.values(offlineData),
          pagination: {
            total: Object.keys(offlineData).length,
            currentPage: 1,
            totalPages: 1,
          },
          cacheKey,
        };
      }
      return rejectWithValue({ message: "You are offline" });
    }

    // Check cache
    const lastFetched = state.courses.lastFetched[cacheKey] || 0;
    if (Date.now() - lastFetched < CACHE_DURATION) {
      return null;
    }

    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const queryParams = new URLSearchParams({
          page: String(params.page || 1),
          limit: String(params.limit || 10),
          ...(params.category && { category: params.category }),
          ...(params.level && { level: params.level }),
          ...(params.isPublished !== undefined && {
            isPublished: String(params.isPublished),
          }),
          ...(params.teacherId && { teacherId: String(params.teacherId) }),
        });

        dispatch(setActiveRequest(requestId));
        const rawResponse = await apiService.get<FetchCoursesResponse>(
          `/courses/list?${queryParams}`
        );
        const response = safeValidate(FetchCoursesResponseSchema, rawResponse, "fetchCourses") as unknown as FetchCoursesResponse;

        // Save successful response to offline storage
        const normalizedData =
          response.data?.courses?.reduce(
            (acc, course) => ({
              ...acc,
              [course.id]: course,
            }),
            {}
          ) || {};
        saveToOfflineStorage(normalizedData);

        return { ...response.data, cacheKey };
      } catch (error) {
        retryCount++;
        if (retryCount === MAX_RETRIES) {
          const axiosError = error as AxiosError<{ message: string }>;
          toast.error(
            axiosError.response?.data?.message || "Failed to fetch courses"
          );
          return rejectWithValue({
            message:
              axiosError.response?.data?.message || "Failed to fetch courses",
          });
        }
        // Wait before retrying
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY * retryCount)
        );
      } finally {
        dispatch(removeActiveRequest(requestId));
      }
    }

    return rejectWithValue({ message: "Max retries exceeded" });
  }
);

export const fetchPublicCourses = createAsyncThunk<
  {
    courses: Course[];
    pagination: {
      total: number;
      currentPage: number;
      totalPages: number;
    };
    cacheKey: string;
  } | null,
  QueryParams,
  {
    state: { courses: CourseState };
    rejectValue: { message: string };
  }
>(
  "courses/fetchPublicCourses",
  async (params, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const cacheKey = createCacheKey(params);
    const requestId = `fetch_${cacheKey}`;

    // Check if request is already in progress
    if (state.courses.activeRequests[requestId]) {
      return null; // Skip duplicate request
    }

    // Check offline status
    if (!navigator.onLine) {
      const offlineData = loadFromOfflineStorage();
      if (offlineData) {
        return {
          courses: Object.values(offlineData),
          pagination: {
            total: Object.keys(offlineData).length,
            currentPage: 1,
            totalPages: 1,
          },
          cacheKey,
        };
      }
      return rejectWithValue({ message: "You are offline" });
    }

    // Check cache
    const lastFetched = state.courses.lastFetched[cacheKey] || 0;
    if (Date.now() - lastFetched < CACHE_DURATION) {
      return null;
    }

    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const queryParams = new URLSearchParams({
          page: String(params.page || 1),
          limit: String(params.limit || 10),
          ...(params.category && { category: params.category }),
          ...(params.level && { level: params.level }),
          ...(params.isPublished !== undefined && {
            isPublished: String(params.isPublished),
          }),
          ...(params.teacherId && { teacherId: String(params.teacherId) }),
        });

        dispatch(setActiveRequest(requestId));
        const response = await apiService.get<FetchCoursesResponse>(
          `/courses/public/list?${queryParams}`
        );

        // Save successful response to offline storage
        const normalizedData =
          response.data?.courses?.reduce(
            (acc, course) => ({
              ...acc,
              [course.id]: course,
            }),
            {}
          ) || {};
        saveToOfflineStorage(normalizedData);

        return { ...response.data, cacheKey };
      } catch (error) {
        retryCount++;
        if (retryCount === MAX_RETRIES) {
          const axiosError = error as AxiosError<{ message: string }>;
          toast.error(
            axiosError.response?.data?.message || "Failed to fetch courses"
          );
          return rejectWithValue({
            message:
              axiosError.response?.data?.message || "Failed to fetch courses",
          });
        }
        // Wait before retrying
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY * retryCount)
        );
      } finally {
        dispatch(removeActiveRequest(requestId));
      }
    }

    return rejectWithValue({ message: "Max retries exceeded" });
  }
);

// Enhanced update course with offline support
export const updateCourse = createAsyncThunk<
  { id: number; updates: Course; optimisticId: string; isOffline?: boolean },
  { id: number; updates: Partial<Course> },
  {
    rejectValue: { error: any; optimisticId: string; id: number };
  }
>(
  "courses/updateCourse",
  async ({ id, updates }, { dispatch, rejectWithValue }) => {
    const optimisticId = `update_${id}_${Date.now()}`;

    // If offline, queue the update
    if (!navigator.onLine) {
      dispatch(
        addToOfflineQueue({
          type: "UPDATE",
          payload: { id, updates },
          timestamp: Date.now(),
        })
      );
      return { id, updates: updates as Course, optimisticId, isOffline: true };
    }

    try {
      // Server wraps the body: { status, message, data: <Course> }
      // apiService.put returns response.data (the full wrapper), so unwrap .data
      const raw = await apiService.put<{ data: Course } & Record<string, any>>(
        `/courses/update-course/${id}`,
        updates
      );
      const updatedCourse: Course = (raw as any).data ?? (raw as any);
      toast.success("Course updated successfully");
      return { id, updates: updatedCourse, optimisticId };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to update course"
      );
      return rejectWithValue({
        error: axiosError.response?.data,
        optimisticId,
        id,
      });
    }
  }
);

//Enhanced delete course
export const deleteCourse = createAsyncThunk<
  { id: number; optimisticId: string; isOffline?: boolean },
  { id: number },
  {
    rejectValue: { error: any; optimisticId: string; id: number };
  }
>(
  "courses/deleteCourse",
  async ({ id }, { dispatch, rejectWithValue }) => {
    const optimisticId = `delete_${id}_${Date.now()}`;

    // If offline, queue the delete
    if (!navigator.onLine) {
      dispatch(
        addToOfflineQueue({
          type: "DELETE",
          payload: { id },
          timestamp: Date.now(),
        })
      );
      return { id, optimisticId, isOffline: true };
    }

    try {
      await apiService.delete(`/courses/delete-course/${id}`);
      toast.success("Course deleted successfully");
      return { id, optimisticId };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to delete course"
      );
      return rejectWithValue({
        error: axiosError.response?.data,
        optimisticId,
        id,
      });
    }
  }
);


// Process offline queue when back online
export const processOfflineQueue = createAsyncThunk(
  "courses/processOfflineQueue",
  async (_, { getState, dispatch }) => {
    const state = getState() as { courses: CourseState };
    const queue = [...state.courses.offlineQueue];

    for (const item of queue) {
      try {
        switch (item.type) {
          case "UPDATE":
            await dispatch(updateCourse(item.payload)).unwrap();
            break;
          case "DELETE":
            await dispatch(deleteCourse(item.payload)).unwrap();
            break;
            case "DELETE":
  await dispatch(deleteCourse({ id: item.payload.id })).unwrap();
  break;
        }
        dispatch(removeFromOfflineQueue(item));
      } catch (error) {
        console.error("Failed to process offline item:", error);
      }
    }
  }
);

const courseSlice = createSlice({
  name: "courses",
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setCourseId: (state, action: PayloadAction<number>) => {
      state.courseId = action.payload;
    },
    invalidateCache: (state) => {
      state.lastFetched = {};
    },
    setActiveRequest: (state, action: PayloadAction<string>) => {
      state.activeRequests[action.payload] = true;
    },
    removeActiveRequest: (state, action: PayloadAction<string>) => {
      delete state.activeRequests[action.payload];
    },
    addToOfflineQueue: (state, action: PayloadAction<OfflineQueueItem>) => {
      state.offlineQueue.push(action.payload);
      saveToOfflineStorage(state.courses);
    },
    removeFromOfflineQueue: (
      state,
      action: PayloadAction<OfflineQueueItem>
    ) => {
      state.offlineQueue = state.offlineQueue.filter(
        (item) => item.timestamp !== action.payload.timestamp
      );
    },
    resetCourses: (state) => {
      state.courses = {};
      state.publicCourses = {};
      state.total = 0;
      state.currentPage = 1;
      state.status = "idle";
      state.error = null;
      state.lastFetched = {};
      state.pendingUpdates = {};
      state.offlineQueue = [];
      state.activeRequests = {};
      state.retryCount = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCourse.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createCourse.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Add the new course to the courses object
        state.courses[action.payload.id] = action.payload;
        // Increment total count
        state.total += 1;
      })
      .addCase(createCourse.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to create course";
      })
      .addCase(fetchCourses.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        if (action.payload) {
          const { courses, pagination, cacheKey } = action.payload;

          // Normalize courses data into object
          state.courses =
            courses?.reduce(
              (acc, course) => ({
                ...acc,
                [course.id]: course,
              }),
              {}
            ) || {};

          // Update pagination info
          state.total = pagination.total;
          state.currentPage = pagination.currentPage;

          // Update cache timestamp
          state.lastFetched[cacheKey] = Date.now();

          state.status = "succeeded";
        }else {
          state.status = "succeeded";
        }
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch courses";
      })
      .addCase(processOfflineQueue.fulfilled, (state) => {
        state.offlineQueue = [];
      })

      .addCase(fetchPublicCourses.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicCourses.fulfilled, (state, action) => {
        if (action.payload) {
          const { courses, pagination, cacheKey } = action.payload;

          // Normalize courses data into object
          state.publicCourses =
            courses?.reduce(
              (acc, course) => ({
                ...acc,
                [course.id]: course,
              }),
              {}
            ) || {};

          // Update pagination info
          state.total = pagination?.total;
          state.currentPage = pagination?.currentPage;

          // Update cache timestamp
          state.lastFetched[cacheKey] = Date.now();

          state.status = "succeeded";
        }else {
          state.status = "succeeded";
        }
      })
      .addCase(fetchPublicCourses.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch courses";
      })

      .addCase(updateCourse.fulfilled, (state, action) => {
        if (!action.payload.isOffline) {
          state.courses[action.payload.id] = action.payload.updates;
        }
      })
     .addCase(deleteCourse.fulfilled, (state, action) => {
  if (!action.payload.isOffline) {
    delete state.courses[action.payload.id];
    state.total = Math.max(0, state.total - 1);
  }
})
.addCase(deleteCourse.rejected, (state, action) => {
  // Handle delete failure if needed
  state.status = "failed";
  state.error = action.payload?.error?.message || "Failed to delete course";
})

  },
});

// Export actions
export const {
  setCurrentPage,
  setCourseId,
  invalidateCache,
  setActiveRequest,
  removeActiveRequest,
  addToOfflineQueue,
  removeFromOfflineQueue,
  resetCourses,
} = courseSlice.actions;

// Selectors with proper typing
export const selectCourses = createSelector(
  [(state: { courses: CourseState }) => state.courses.courses],
  (courses): Course[] => Object.values(courses)
);

export const selectPublicCourses = createSelector(
  [(state: { courses: CourseState }) => state.courses.publicCourses],
  (courses): Course[] => Object.values(courses)
);

export const selectCourseId = createSelector(
  [(state: { courses: CourseState }) => state.courses],
  (courses: CourseState): number | null => courses.courseId ?? null
);

export const selectCourseById = createSelector(
  [
    (state: { courses: CourseState }) => state.courses.courses,
    (_: any, id: number) => id,
  ],
  (courses, id): Course | undefined => courses[id]
);

export const selectPublicCoursesById = createSelector(
  [
    (state: { courses: CourseState }) => state.courses.publicCourses,
    (_: any, id: number) => id,
  ],
  (courses, id): Course | undefined => courses[id]
);

export const selectTotalStudents = createSelector(
  [(state: { courses: CourseState }) => state.courses.courses],
  (courses): number => {
    return Object.values(courses).reduce((total, course) => {
      return total + (course.enrolledStudents || 0);
    }, 0);
  }
);

// Setup online/offline listeners
export const setupNetworkListeners = (store: any) => {
  window.addEventListener("online", () => {
    store.dispatch(processOfflineQueue());
  });

  window.addEventListener("offline", () => {
    toast.info("You are offline. Changes will be saved locally.");
  });
};

export default courseSlice.reducer;
